/**
 * Given an image element, find the largest size using the src and srcset attributes.
 *
 * @param {HTML_Element} image The Image element.
 * @returns {object} An object containing the largest image's attributes.
 */
export default function getLargestImageSize(image) {
  const {
    src,
    width,
    naturalWidth,
    height,
    naturalHeight,
    srcset,
  } = image;

  const imgRatio = (width / height);

  /*
   * The natural height/width of the image, used as a fallback for unknown
   * dimensions. This assumes the image src includes a query string to
   * dynamically resize the full-size image, so we rebuild the src URL to omit
   * the query string.
   */
  const { origin, pathname } = new URL(src);
  const naturalSize = {
    src: `${origin}${pathname}`,
    width: naturalWidth,
    height: naturalHeight,
  };

  if ('' === srcset) {
    return naturalSize;
  }

  // Create an object mapping of width descriptors and their img src.
  const srcsetObj = srcset.split(',').reduce((acc, value) => {
    const [url, size] = value.trim().split(' ');

    // Strip the `w` from the end.
    const newKey = size.substring(0, size.length - 1);

    return {
      ...acc,
      [newKey]: url,
    };
  }, {});

  // Sort the keys to get the largest.
  const largestWidthDescriptor = Object.keys(srcsetObj)
    .sort(() => (a, b) => a - b) // eslint-disable-line id-length
    .pop();

  if (srcsetObj?.[largestWidthDescriptor]) {
    return {
      src: srcsetObj[largestWidthDescriptor],
      width: parseInt(largestWidthDescriptor, 10),
      height: Math.round(parseInt(largestWidthDescriptor, 10) / imgRatio),
    };
  }

  return naturalSize;
}
